import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Battery, BatteryLow, BatteryFull, Clock, Utensils, ChevronRight, X, Loader2 } from 'lucide-react';
import axios from 'axios';
import logger from '@/utils/logger';

const API = '/api';

const C = {
  bg: '#FDFBF7', card: '#FFFFFF', border: '#E8E3DB', text: '#1A1A18',
  muted: '#9A9590', green: '#5A8C6A', accent: '#C67A5C', cream: '#F5F0E8',
  gold: '#E5A84B',
};

const ENERGY_OPTIONS = [
  {
    id: 'zero',
    icon: Battery,
    title: 'Zero energy',
    desc: 'Something I can eat in under 5 min with zero cooking',
    color: '#C67A5C',
  },
  {
    id: 'little',
    icon: BatteryLow,
    title: 'A little energy',
    desc: '15 min or less, minimal cleanup',
    color: C.gold,
  },
  {
    id: 'ambitious',
    icon: BatteryFull,
    title: "I'm feeling ambitious",
    desc: "I'll actually cook something tonight",
    color: C.green,
  },
];

export default function JustTellMeModal({ isOpen, onClose }) {
  const navigate = useNavigate();
  const { authHeaders } = useAuth();
  const [step, setStep] = useState('energy'); // 'energy' | 'results'
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [selectedEnergy, setSelectedEnergy] = useState(null);

  if (!isOpen) return null;

  const handleEnergySelect = async (energy) => {
    setSelectedEnergy(energy);
    setLoading(true);
    try {
      const r = await axios.post(`${API}/gentle/just-tell-me`, { energy_level: energy }, authHeaders());
      setResults(r.data);
      setStep('results');
    } catch (e) {
      logger.error('Just tell me failed:', e);
    }
    setLoading(false);
  };

  const handlePickMeal = (suggestion) => {
    onClose();
    if (suggestion.recipe_id) {
      navigate(`/recipe/${suggestion.recipe_id}`);
    }
    // For zero-effort meals, just close — they have instructions inline
  };

  const handleClose = () => {
    setStep('energy');
    setResults(null);
    setSelectedEnergy(null);
    onClose();
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200,
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    }}
      data-testid="just-tell-me-modal"
    >
      <div style={{
        background: C.bg, borderRadius: '24px 24px 0 0', width: '100%', maxWidth: 480,
        maxHeight: '85vh', overflow: 'auto', padding: '24px 24px 100px',
        animation: 'slideUp 0.3s ease-out',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: C.text, fontFamily: 'var(--font-sans)', margin: 0 }}>
            {step === 'energy' ? 'Just Tell Me What to Eat' : results?.message || 'Here you go'}
          </h2>
          <button data-testid="just-tell-me-close" onClick={handleClose} style={{ padding: 6, background: 'none', border: 'none', cursor: 'pointer' }}>
            <X style={{ width: 20, height: 20, color: C.muted }} />
          </button>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <Loader2 style={{ width: 28, height: 28, color: C.green, margin: '0 auto 12px', animation: 'spin 1s linear infinite' }} />
            <p style={{ fontSize: 14, color: C.muted, fontFamily: 'var(--font-sans)' }}>Finding the perfect meal...</p>
          </div>
        )}

        {/* Step 1: Energy Level */}
        {step === 'energy' && !loading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <p style={{ fontSize: 14, color: C.muted, fontFamily: 'var(--font-sans)', margin: '0 0 8px' }}>
              How much energy do you have right now?
            </p>
            {ENERGY_OPTIONS.map(opt => (
              <button
                key={opt.id}
                data-testid={`energy-${opt.id}`}
                onClick={() => handleEnergySelect(opt.id)}
                style={{
                  padding: '16px 18px', borderRadius: 16, background: C.card,
                  border: `1px solid ${C.border}`, cursor: 'pointer', textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 14,
                  transition: 'border-color 0.2s',
                }}
              >
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: `${opt.color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <opt.icon style={{ width: 22, height: 22, color: opt.color }} />
                </div>
                <div>
                  <p style={{ fontSize: 15, fontWeight: 600, color: C.text, fontFamily: 'var(--font-sans)', margin: 0 }}>
                    {opt.title}
                  </p>
                  <p style={{ fontSize: 12, color: C.muted, fontFamily: 'var(--font-sans)', margin: '2px 0 0' }}>
                    {opt.desc}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Step 2: Results */}
        {step === 'results' && !loading && results && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {results.suggestions?.map((suggestion, i) => (
              <button
                key={suggestion.meal_id || suggestion.recipe_id || i}
                data-testid={`suggestion-${i}`}
                onClick={() => handlePickMeal(suggestion)}
                style={{
                  padding: '16px 18px', borderRadius: 16, background: C.card,
                  border: `1px solid ${C.border}`, cursor: 'pointer', textAlign: 'left',
                  transition: 'border-color 0.2s',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 15, fontWeight: 600, color: C.text, fontFamily: 'var(--font-sans)', margin: 0 }}>
                      {suggestion.title}
                    </p>
                    {suggestion.description && (
                      <p style={{ fontSize: 12, color: C.muted, fontFamily: 'var(--font-sans)', margin: '4px 0 0', lineHeight: 1.4 }}>
                        {suggestion.description}
                      </p>
                    )}
                    <div style={{ display: 'flex', gap: 10, marginTop: 8, fontSize: 11, color: C.muted, fontFamily: 'var(--font-sans)' }}>
                      <span style={{ color: C.accent, fontWeight: 600 }}>{suggestion.calories} cal</span>
                      <span>{suggestion.protein}g protein</span>
                      {suggestion.prep_time !== undefined && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Clock style={{ width: 10, height: 10 }} />
                          {(suggestion.prep_time || 0) + (suggestion.cook_time || 0)} min
                        </span>
                      )}
                    </div>
                    {/* Show zero-effort instructions inline */}
                    {selectedEnergy === 'zero' && suggestion.instructions && (
                      <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
                        <ol style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: C.muted, fontFamily: 'var(--font-sans)', lineHeight: 1.7 }}>
                          {suggestion.instructions.map((step, j) => <li key={j}>{step}</li>)}
                        </ol>
                      </div>
                    )}
                  </div>
                  {suggestion.recipe_id && (
                    <ChevronRight style={{ width: 18, height: 18, color: C.muted, marginTop: 2, flexShrink: 0 }} />
                  )}
                </div>
              </button>
            ))}

            {/* Try different energy */}
            <button
              data-testid="try-different-energy"
              onClick={() => { setStep('energy'); setResults(null); }}
              style={{
                padding: '12px', borderRadius: 12, background: 'none', border: `1px dashed ${C.border}`,
                color: C.muted, fontSize: 13, fontFamily: 'var(--font-sans)', cursor: 'pointer', textAlign: 'center',
              }}
            >
              Different energy level?
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
